define('core/models/Session',[
    'jquery',
    'underscore',
    'backbone',
    'moment',

    'core/api',
    'core/bus',
    'core/config',
    'core/time',
    'core/utils',
    'core/utils/cookies',
    'core/utils/guid',
    'core/utils/auth',

    'core/models/BaseUser',
    'core/models/User',
], function (
    $,
    _,
    Backbone,
    moment,

    api,
    bus,
    config,
    time,
    utils,
    cookies,
    guid,
    auth,

    BaseUser,
    User
) {
    'use strict';

    var fromCookie = function () {
        // Can't move implementation here, as it is imported via auth into React
        // applications. This should be the single access point for this function
        // across embed/home/next-core.
        return auth.getFromCookie();
    };

    /**
     * Session is a utility wrapper around a User instance.
     *
     * Usage:
     *
     *   var session = new Session(); // logged out (BaseUser)
     *   var user = new User({ username: 'janedoe' });
     *   session.setUser(user); // logged in
     *
     *
     * You can bind to the session "change" event like so:
     *
     *   session.on('change:id', this.handleSessionChange);
     *
     *
     * Any events fired on the wrapped User model also fire on the Session instance:
     *
     *   session.on('change:display_name', this.handleNameChange);
     *   user.set('display_name', 'Jane Doe'); // => triggers handler
     *
     */
    var Session = Backbone.Model.extend({

        initialize: function () {
            // Do the once wrapping upon initialization, not statically, so that
            // fromCookie can be tested. Otherwise it can only be called once per
            // test run.
            this.constructor.fromCookie = _.once(fromCookie);
            this.user = this.getAnonUserInstance();
        },

        setUser: function (user) {
            if (this.user)
                this.stopListening(this.user);

            this.user = user;
            this.setIfNewUser();
            this.listenTo(user, 'all', this.trigger);

            this.trigger('change:id', user);
        },

        isLoggedOut: function () {
            return !this.isLoggedIn();
        },

        /**
         * Is the current session authenticated with the server?
         *
         * The current logic just checks if the session user has an ID,
         * but we may change the underlying logic in the future to support
         * situations where we don't yet have the user ID but know that the
         * session is authenticated (of vice versa).
         *
         * @returns {boolean}
         */
        isLoggedIn: function () {
            return Boolean(this.user.get('id'));
        },

        fetch: function (options) {
            var opts = options || {};

            return api.call('users/details.json', {
                data: opts.data,
                success: _.bind(function (response) {
                    response = response.response;

                    if (response.id)
                        this.setUser(this.getUserInstance(response));

                    if (opts.success) opts.success(response);
                    if (opts.complete) opts.complete(response);
                }, this),

                error: function (response) {
                    if (opts.error) opts.error(response);
                    if (opts.complete) opts.complete(response);
                },
            });
        },

        getAnonUserInstance: function (attrs) {
            return new BaseUser(attrs);
        },

        getUserInstance: function (attrs) {
            return new User(attrs);
        },

        getCsrfToken: function () {
            var token = cookies.read('csrftoken');

            // We may not always have a token set so if is empty, we set it
            // ourselves. Since Django only compares the token passed in the
            // URL with the value from the cookie, this just works :)
            if (!token) {
                // Django expects the CSRF token to only contain alphanumeric
                // characters, and everything else gets replaced.
                token = guid.generate().replace(/\W/g, '');
                cookies.create('csrftoken', token, {
                    domain: window.location.hostname,
                    expiresIn: 365 * 24 * 60 * 60 * 1000,  // One year
                });
            }

            return token;
        },

        authenticate: function (service) {
            /** @type {Authenticator} */
            var authenticator = this.authServices[service];
            if (!authenticator)
                return;

            if (_.isFunction(authenticator))
                return authenticator.call(this);

            // TODO: move this uiAction event out of Session.
            // It belongs w/ the event handler method that triggers
            // authenticate.
            bus.trigger('uiAction:openLogin', service);

            var windowArgs = this.getAuthWindowArgs(authenticator);
            var url = authenticator.url;
            url += (url.indexOf('?') > -1 ? '&' : '?') + $.param(windowArgs);

            this.openAuthWindow(
                url,
                authenticator.width,
                authenticator.height
            );
        },

        /**
         * An authentication service.
         * @typedef {Object} Authenticator
         * @property {string} url - URL of popup window to initiate authentication.
         * @property {number} width - Width of popup window.
         * @property {number} height - Height of popup window.
         * @property {Object} [params] - Additional parameters to pass to window.
         * @property {boolean} [csrf] - Whether to attach CSRF token as parameter.
         * @property {boolean} [attachExperiment] - Whether to attach experiment
         *     information as a parameter (embed-only).
         */

        authServices: {
            /** @type {Authenticator} */
            disqus: {
                url: config.urls.login,
                width: 460,
                height: 355,
                attachExperiment: true,
            },

            /** @type {Authenticator} */
            twitter: {
                url: config.urls.oauth.twitter,
                width: 650,
                height: 680,
                csrf: true,
                attachExperiment: true,
            },

            /** @type {Authenticator} */
            facebook: {
                url: config.urls.oauth.facebook,
                width: 550,
                height: 300,
                csrf: true,
                attachExperiment: true,
            },

            /** @type {Authenticator} */
            google: {
                url: config.urls.oauth.google,
                width: 445,
                height: 635,
                csrf: true,
                attachExperiment: true,
            },
        },

        /**
         * @param {Authenticator} authenticator - Authentication service.
         * @returns {string}
         */
        getAuthWindowArgs: function (authenticator) {
            var windowArgs = {};

            if (authenticator.csrf)
                windowArgs.ctkn = this.getCsrfToken();

            _.extend(windowArgs, authenticator.params);

            return windowArgs;
        },

        openAuthWindow: function (url, width, height) {
            return utils.openWindow(url, '_blank', {
                width: width,
                height: height,
            });
        },

        /**
         * Tries to determine if session user is a newly registered user and sets a
         * joinedRecently attribute on the user model accordingly.
         *
         * We assume a user is newly registered if account was created in the last 10s.
         */
        setIfNewUser: function () {
            var joinedAt = this.user.get('joinedAt');

            // set to false if user is anonymous or doesn't have a joinedAt date set
            if (this.user.get('isAnonymous') || !joinedAt) {
                this.user.set('joinedRecently', false);
                return;
            }

            var joinedAtUtc = time.assureTzOffset(joinedAt);

            // if user joined in the last 10s, set joinedRecently on user model to true
            this.user.set('joinedRecently', moment().subtract(10, 'seconds').isBefore(joinedAtUtc));
        },

    });

    /**
     * Get session attributes from the `disqusauth` cookie.
     *
     * The `disqusauth` cookie is present on disqus.com whenever
     * a user is logged in with a valid session. It is safe to
     * assume that a user without a disqusauth cookie is not logged
     * in.
     *
     * The `disqusauth` cookie conflates session info with session
     * user info.
     *
     * @returns {Object} Set of session attributes from cookie
     */
    Session.fromCookie = fromCookie;

    /**
     * Checks the disqusauth cookie to see if no authenticated session exists.
     *
     * @returns {boolean}
     */
    Session.isKnownToBeLoggedOut = function () {
        return !Session.fromCookie().id;
    };

    return Session;
});

